// additional marketing parameters to put into the mixpanel session if available
// that are not added by mixpanel by default.
//
// common identifiers https://en.wikipedia.org/wiki/Click_identifier
const additionalParams = [
  'dclid', // doubleclick
  'fbclid', // facebook
  'gbraid', // google
  'gclid', // google
  'gclsrc', // google
  'li_fat_id', // linkedin
  'msclkid', // microsoft/bing
  'sl_track_id', // sproutloud
  'ttclid', // tiktok
  'twclid', // twitter
  'wbraid', // google
  'yclid', // yahoo and yandex
];

// sb.siteInfo keys to update on each request
const siteInfoKeysToTrack = [
  'page_name'
];

// sb.siteInfo keys to track once
const siteInfoKeysToTrackOnce = [
  'site_id',
  'formatted_domain',
  'domain',
  'base_directory',
  'whitelabel_id',
  'theme_id',
  'partner_sub_id',
  'subdomain'
];

const detectSearchEngine = (referrer) => {
  // Detect if the referrer is a search engine
  if (!referrer) return null;

  const searchEngines = {
    'google.': 'google',
    'bing.': 'bing',
    'yahoo.': 'yahoo',
    'yandex.': 'yandex',
    'baidu.': 'baidu',
    'duckduckgo.': 'duckduckgo'
  };

  for (const domain in searchEngines) {
    if (referrer.indexOf(domain) !== -1) {
      return searchEngines[domain];
    }
  }

  return null;
};

const getReferrerInfo = (document) => {
  // parse referrer info
  const referrer = document.referrer;
  const result = {
    $initial_referrer: referrer || '$direct',
    $initial_referring_domain: '$direct',
  };

  if (referrer) {
    try {
      const url = new URL(referrer);
      result.$initial_referring_domain = url.hostname;

      // Detect search engine
      const searchEngine = detectSearchEngine(referrer);
      if (searchEngine) {
        result.$search_engine = searchEngine;
      }
    } catch (e) {
      // In case of error, keep the defaults
    }
  }

  return result;
}

const parseTrackingParams = (window) => {
  // parse tracking params from url
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const trackingParams = {};
  params
    .forEach((value, key) => {
      const lowerKey = key.toLowerCase();
      // utm and additional params
      if (!lowerKey.startsWith('utm_') && !additionalParams.includes(lowerKey)) {
        return;
      }
      trackingParams[lowerKey] = value;
    });
  return trackingParams;
}


const generateUUID = () => {
  // Generate UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // where x is any hexadecimal digit and y is one of 8, 9, A, or B
  const hexDigits = '0123456789abcdef';
  let uuid = '';

  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      uuid += '-';
    } else if (i === 14) {
      uuid += '4';  // Version 4 UUID always has a 4 here
    } else if (i === 19) {
      uuid += hexDigits[(Math.random() * 4 + 8) | 0];  // 8, 9, a, or b
    } else {
      uuid += hexDigits[(Math.random() * 16) | 0];
    }
  }
  
  return uuid;
};


const siteInfoKeys = (window,keys) => {
  // get site info keys
  const siteInfo = window.sb.siteInfo;
  return keys.reduce((acc, key) => {
    acc[key] = siteInfo[key];
    return acc;
  }, {});
}


// Cookie storage implementation
const CookieStorage = {
  // Get a cookie
  get: function(name) {
    const cookies = document.cookie.split(';');
    const target = name + '=';

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(target) === 0) {
        return decodeURIComponent(cookie.substring(target.length, cookie.length));
      }
    }
    return null;
  },

  // Parse a cookie into a javascript object
  parse: function(name) {
    let cookie = this.get(name);
    if (!cookie) return {};
    try {
      return JSON.parse(cookie) || {};
    } catch (e) {
      return {};
    }
  },

  // Set a cookie
  set: function(name, value, days, crossSubdomain, secure, sameSite) {
    let cdomain = '', expires = '', sameSiteValue = '', secureValue = '';

    // Handle expiry
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toGMTString();
    }

    // Handle domain
    if (crossSubdomain) {
      const hostname = window.location.hostname;
      const domainParts = hostname.split('.');
      if (domainParts.length > 1) {
        cdomain = '; domain=.' + domainParts.slice(-2).join('.');
      }
    }

    // Handle same site and secure
    if (sameSite) {
      sameSiteValue = '; SameSite=' + sameSite;
    }

    if (secure) {
      secureValue = '; secure';
    }

    // Set the cookie
    document.cookie = name + '=' + encodeURIComponent(value) + expires + 
                        '; path=/' + cdomain + sameSiteValue + secureValue;
    return true;
  },

  // Remove a cookie
  remove: function(name, crossSubdomain) {
    this.set(name, '', -1, crossSubdomain, false);
  }
};


const SessionAdaptor = (adaptorPayload) => {
  // Basic session tracking functionality to capture attribution and page views

  if (adaptorPayload.analyticsEvent.name != 'site-page-view') {
    // only action on page views and not normal events
    return false;
  }

  // name of cookie to store session data
  let testPrefix = '';
  if (adaptorPayload.options.session && adaptorPayload.options.session.test_mode) {
    testPrefix = 'test_';
  }
  const storageKey = `${testPrefix}mp_${window.sb.siteInfo.analytics_token}_mixpanel`;
  // storage implementation
  const storage = CookieStorage;

  // get existing payload
  let existingProps = {
    ...storage.parse(storageKey)
  }

  if (Object.keys(existingProps).length === 0) {
    // start of new session, initialize props
    const deviceId = generateUUID();
    existingProps = {
      $device_id: deviceId,
      distinct_id: `$device:${deviceId}`,
      initial_landing_page: window.location.href,
      ...getReferrerInfo(document),
      ...parseTrackingParams(window),
      ...siteInfoKeys(window, siteInfoKeysToTrackOnce),
    }
  }

  // add continually updated props
  existingProps = {
    ...existingProps,
    full_url: window.location.href,
    page_title: document.title,
    // increment page view count
    'site-session-page-views': existingProps['site-session-page-views'] ? existingProps['site-session-page-views'] + 1 : 1,
    url: window.location.pathname,
    ...siteInfoKeys(window, siteInfoKeysToTrack),
  }

  const defaultExpiry = 365;
  const crossSubdomain = false;
  const secureCookie = false;
  const sameSite = null;
  const jsonValue = JSON.stringify(existingProps);
  storage.set(
    storageKey,
    jsonValue,
    defaultExpiry,
    crossSubdomain,
    secureCookie,
    sameSite
  )

  return true;
}

export { SessionAdaptor }